import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, UserPlus, CheckCircle2, Clock, AlertCircle, RefreshCw, Search, BarChart3, Settings, Eye, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';

interface Task {
  id: number;
  title: string;
  description: string | null;
  taskType: 'aviso' | 'validacion' | 'respuesta_texto';
  moduleTarget: string | null;
  createdByName: string | null;
  createdAt: string;
  assignedCount: number;
  completedCount: number;
}

interface Role {
  id: number;
  name: string;
}

interface User {
  id: number;
  fullName: string;
  email: string;
}

interface TrackingAssignment {
  userTaskId: number;
  taskId: number;
  taskTitle: string;
  taskDescription: string | null;
  taskType: string;
  moduleTarget: string | null;
  status: string;
  completedAt: string | null;
  completionResponse: string | null;
  assignedAt: string;
  userId: number;
  userName: string;
  userEmail: string;
  roleId: number;
  roleName: string | null;
}

interface TrackingSummary {
  totalAssignments: number;
  completedAssignments: number;
  pendingAssignments: number;
  completionRate: number;
}

interface TaskProgress {
  taskId: number;
  title: string;
  taskType: string;
  total: number;
  completed: number;
  completionRate: number;
}

interface RoleProgress {
  roleName: string;
  total: number;
  completed: number;
  completionRate: number;
}

const TaskManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'gestion' | 'seguimiento'>('gestion');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAssign, setCanAssign] = useState(false);
  const { toast } = useToast();

  const [trackingAssignments, setTrackingAssignments] = useState<TrackingAssignment[]>([]);
  const [trackingSummary, setTrackingSummary] = useState<TrackingSummary>({ totalAssignments: 0, completedAssignments: 0, pendingAssignments: 0, completionRate: 0 });
  const [taskProgress, setTaskProgress] = useState<TaskProgress[]>([]);
  const [roleProgress, setRoleProgress] = useState<RoleProgress[]>([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingRoleFilter, setTrackingRoleFilter] = useState<string>('');
  const [trackingSearch, setTrackingSearch] = useState('');
  const [trackingStatusFilter, setTrackingStatusFilter] = useState<string>('todas');
  const [showResponseDialog, setShowResponseDialog] = useState<TrackingAssignment | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    taskType: 'aviso' as 'aviso' | 'validacion' | 'respuesta_texto',
    moduleTarget: '',
  });

  const [assignData, setAssignData] = useState({
    assignmentType: 'role' as 'role' | 'user' | 'all_users',
    roleId: '',
    userId: '',
  });

  useEffect(() => {
    fetchTasks();
    fetchRoles();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'seguimiento') {
      fetchTracking();
    }
  }, [activeTab]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tasks', { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar tareas');
      const data = await response.json();
      setTasks(data.tasks);
      if (typeof data.isAdmin === 'boolean') {
        setIsAdmin(data.isAdmin);
      }
      if (typeof data.canAssign === 'boolean') {
        setCanAssign(data.canAssign);
      }
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Error al cargar tareas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetch('/api/roles', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const rolesList = Array.isArray(data) ? data : [];
        setRoles(rolesList);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/users', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const usersList = Array.isArray(data) ? data : [];
        setUsers(usersList);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchTracking = useCallback(async () => {
    try {
      setTrackingLoading(true);
      const params = new URLSearchParams();
      if (trackingRoleFilter) params.set('roleId', trackingRoleFilter);
      if (trackingSearch) params.set('search', trackingSearch);
      const url = `/api/tasks/tracking${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar seguimiento');
      const data = await response.json();
      setTrackingAssignments(data.assignments || []);
      setTrackingSummary(data.summary || { totalAssignments: 0, completedAssignments: 0, pendingAssignments: 0, completionRate: 0 });
      setTaskProgress(data.byTask || []);
      setRoleProgress(data.byRole || []);
      setLastRefreshed(new Date());
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar los datos de seguimiento', variant: 'destructive' });
    } finally {
      setTrackingLoading(false);
    }
  }, [trackingRoleFilter, trackingSearch, toast]);

  const handleRefreshTracking = () => {
    fetchTracking();
    toast({ title: 'Actualizado', description: 'Datos de seguimiento actualizados' });
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title) {
      toast({ title: 'Error', description: 'El título es requerido', variant: 'destructive' });
      return;
    }
    if (formData.taskType === 'validacion' && !formData.moduleTarget) {
      toast({ title: 'Error', description: 'El módulo es requerido para tareas de validación', variant: 'destructive' });
      return;
    }
    try {
      const response = await fetch(selectedTask ? `/api/tasks/${selectedTask.id}` : '/api/tasks', {
        method: selectedTask ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Error al guardar tarea');
      toast({ title: 'Éxito', description: selectedTask ? 'Tarea actualizada correctamente' : 'Tarea creada correctamente' });
      setShowCreateDialog(false);
      setFormData({ title: '', description: '', taskType: 'aviso', moduleTarget: '' });
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Error al guardar tarea', variant: 'destructive' });
    }
  };

  const handleAssignTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask) return;
    const payload: any = { assignmentType: assignData.assignmentType };
    if (assignData.assignmentType === 'role') {
      payload.roleId = parseInt(assignData.roleId);
    } else if (assignData.assignmentType === 'user') {
      payload.userId = parseInt(assignData.userId);
    }
    try {
      const response = await fetch(`/api/tasks/${selectedTask.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Error al asignar tarea');
      const result = await response.json();
      toast({ title: 'Éxito', description: result.message || 'Tarea asignada correctamente' });
      setShowAssignDialog(false);
      setAssignData({ assignmentType: 'role', roleId: '', userId: '' });
      fetchTasks();
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Error al asignar tarea', variant: 'destructive' });
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta tarea?')) return;
    try {
      const response = await fetch(`/api/tasks/${taskId}`, { method: 'DELETE', credentials: 'include' });
      if (!response.ok) throw new Error('Error al eliminar tarea');
      toast({ title: 'Éxito', description: 'Tarea eliminada correctamente' });
      fetchTasks();
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Error al eliminar tarea', variant: 'destructive' });
    }
  };

  const openEditDialog = (task: Task) => {
    setSelectedTask(task);
    setFormData({ title: task.title, description: task.description || '', taskType: task.taskType, moduleTarget: task.moduleTarget || '' });
    setShowCreateDialog(true);
  };

  const openCreateDialog = () => {
    setSelectedTask(null);
    setFormData({ title: '', description: '', taskType: 'aviso', moduleTarget: '' });
    setShowCreateDialog(true);
  };

  const openAssignDialog = useCallback((task: Task) => {
    fetchRoles();
    fetchUsers();
    setSelectedTask(task);
    setAssignData({ assignmentType: 'role', roleId: '', userId: '' });
    setShowAssignDialog(true);
  }, [fetchRoles, fetchUsers]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getTaskTypeBadge = (type: string) => {
    if (type === 'aviso') return { label: 'Aviso', cls: 'bg-blue-100 text-blue-700' };
    if (type === 'respuesta_texto') return { label: 'Respuesta de Texto', cls: 'bg-amber-100 text-amber-700' };
    return { label: 'Validación', cls: 'bg-purple-100 text-purple-700' };
  };

  const filteredTrackingAssignments = trackingAssignments.filter(a => {
    if (trackingStatusFilter === 'pendiente' && a.status !== 'pendiente') return false;
    if (trackingStatusFilter === 'completada' && a.status !== 'completada') return false;
    return true;
  });

  const getBarColor = (pct: number, variant: 'blue' | 'indigo') => {
    if (pct === 100) return 'bg-green-500';
    if (pct >= 50) return variant === 'indigo' ? 'bg-indigo-500' : 'bg-blue-500';
    return 'bg-amber-500';
  };

  const renderProgressBar = (completed: number, total: number, variant: 'blue' | 'indigo' = 'blue') => {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
      <div className="w-full">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600">{completed}/{total}</span>
          <span className="font-medium text-gray-800">{pct}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${getBarColor(pct, variant)}`}
            style={{ width: `${pct}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Tareas</h1>
          <p className="text-gray-600 mt-1">
            {(isAdmin || canAssign) ? 'Administra tareas y asignaciones para usuarios del sistema' : 'Administra tus tareas creadas'}
          </p>
        </div>
        {activeTab === 'gestion' && (
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Tarea
          </Button>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('gestion')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'gestion'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Gestión
          </button>
          {(isAdmin || canAssign) && (
            <button
              onClick={() => setActiveTab('seguimiento')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'seguimiento'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
              Seguimiento
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'gestion' && (
        <>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando tareas...</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {tasks.length === 0 ? (
                <Card className="p-12 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No hay tareas creadas</p>
                  <Button onClick={openCreateDialog} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear primera tarea
                  </Button>
                </Card>
              ) : (
                tasks.map((task) => {
                  const badge = getTaskTypeBadge(task.taskType);
                  return (
                    <Card key={task.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                          </div>
                          {task.description && <p className="text-gray-600 mb-3">{task.description}</p>}
                          {task.taskType === 'validacion' && task.moduleTarget && (
                            <p className="text-sm text-gray-500 mb-3">
                              <span className="font-medium">Módulo:</span> {task.moduleTarget}
                            </p>
                          )}
                          {(isAdmin || canAssign) && (
                            <div className="flex items-center gap-6 text-sm">
                              <div className="flex items-center gap-2 text-gray-600">
                                <UserPlus className="w-4 h-4" /><span>{task.assignedCount} asignadas</span>
                              </div>
                              <div className="flex items-center gap-2 text-green-600">
                                <CheckCircle2 className="w-4 h-4" /><span>{task.completedCount} completadas</span>
                              </div>
                              <div className="flex items-center gap-2 text-orange-600">
                                <Clock className="w-4 h-4" /><span>{task.assignedCount - task.completedCount} pendientes</span>
                              </div>
                            </div>
                          )}
                          {task.createdByName && (
                            <p className="text-xs text-gray-500 mt-3">
                              Creada por {task.createdByName} el {new Date(task.createdAt).toLocaleDateString('es-MX')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          {(isAdmin || canAssign) && (
                            <Button variant="outline" size="sm" onClick={() => openAssignDialog(task)}>
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(task)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeleteTask(task.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'seguimiento' && (isAdmin || canAssign) && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={trackingSearch}
                  onChange={(e) => setTrackingSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchTracking(); }}
                  className="pl-9"
                />
              </div>
              <select
                className="border rounded-md px-3 py-2 text-sm min-w-[180px]"
                value={trackingRoleFilter}
                onChange={(e) => {
                  setTrackingRoleFilter(e.target.value);
                  setTimeout(() => fetchTracking(), 0);
                }}
              >
                <option value="">Todos los perfiles</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <Button
                  variant={trackingStatusFilter === 'todas' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrackingStatusFilter('todas')}
                >
                  Todas
                </Button>
                <Button
                  variant={trackingStatusFilter === 'pendiente' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrackingStatusFilter('pendiente')}
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Pendientes
                </Button>
                <Button
                  variant={trackingStatusFilter === 'completada' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrackingStatusFilter('completada')}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Completadas
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {lastRefreshed && (
                <span className="text-xs text-gray-400">
                  {formatDate(lastRefreshed.toISOString())}
                </span>
              )}
              <Button onClick={handleRefreshTracking} variant="outline" size="sm" disabled={trackingLoading}>
                <RefreshCw className={`w-4 h-4 mr-1.5 ${trackingLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>

          {trackingLoading && trackingAssignments.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando datos de seguimiento...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Asignaciones</p>
                      <p className="text-2xl font-bold text-gray-900">{trackingSummary.totalAssignments}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Completadas</p>
                      <p className="text-2xl font-bold text-green-600">{trackingSummary.completedAssignments}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pendientes</p>
                      <p className="text-2xl font-bold text-amber-600">{trackingSummary.pendingAssignments}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${trackingSummary.completionRate >= 75 ? 'bg-green-100' : trackingSummary.completionRate >= 50 ? 'bg-blue-100' : 'bg-amber-100'}`}>
                      <BarChart3 className={`h-5 w-5 ${trackingSummary.completionRate >= 75 ? 'text-green-600' : trackingSummary.completionRate >= 50 ? 'text-blue-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Avance General</p>
                      <p className={`text-2xl font-bold ${trackingSummary.completionRate >= 75 ? 'text-green-600' : trackingSummary.completionRate >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                        {trackingSummary.completionRate}%
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              {taskProgress.length > 0 && (
                <Card className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Avance por Tarea</h3>
                  <div className="space-y-4">
                    {taskProgress.map((tp) => {
                      const badge = getTaskTypeBadge(tp.taskType);
                      return (
                        <div key={tp.taskId} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800 flex-1 truncate">{tp.title}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                          </div>
                          {renderProgressBar(tp.completed, tp.total)}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {roleProgress.length > 0 && (
                <Card className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Avance por Perfil</h3>
                  <div className="space-y-4">
                    {roleProgress.map((rp) => (
                      <div key={rp.roleName} className="space-y-1">
                        <span className="text-sm font-medium text-gray-800">{rp.roleName}</span>
                        {renderProgressBar(rp.completed, rp.total, 'indigo')}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <Card className="overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">
                    Detalle de Asignaciones
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({filteredTrackingAssignments.length} registros)
                    </span>
                  </h3>
                </div>
                {filteredTrackingAssignments.length === 0 ? (
                  <div className="p-8 text-center">
                    <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No hay asignaciones con los filtros seleccionados</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Usuario</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Perfil</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Tarea</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha Asignación</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha Completada</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Respuesta</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredTrackingAssignments.map((a) => {
                          const badge = getTaskTypeBadge(a.taskType);
                          return (
                            <tr key={a.userTaskId} className="hover:bg-gray-50 transition-colors">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-gray-900">{a.userName}</p>
                                  <p className="text-xs text-gray-400">{a.userEmail}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {a.roleName || 'Sin Rol'}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <p className="text-gray-800 max-w-[200px] truncate">{a.taskTitle}</p>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                                  {badge.label}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                {a.status === 'completada' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Completada
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    <Clock className="w-3 h-3" />
                                    Pendiente
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-xs text-gray-500">{formatDate(a.assignedAt)}</td>
                              <td className="py-3 px-4 text-xs text-gray-500">
                                {a.completedAt ? formatDate(a.completedAt) : '—'}
                              </td>
                              <td className="py-3 px-4">
                                {a.completionResponse ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => setShowResponseDialog(a)}
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1" />
                                    Ver
                                  </Button>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      )}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTask ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label htmlFor="title">Título *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>
            <div>
              <Label htmlFor="taskType">Tipo de Tarea *</Label>
              <select
                id="taskType"
                className="w-full border rounded-md px-3 py-2"
                value={formData.taskType}
                onChange={(e) => setFormData({ ...formData, taskType: e.target.value as 'aviso' | 'validacion' | 'respuesta_texto' })}
              >
                <option value="aviso">Aviso (Completar manualmente)</option>
                <option value="validacion">Validación (Completar automáticamente)</option>
                <option value="respuesta_texto">Respuesta de Texto (Requiere valor para completar)</option>
              </select>
            </div>
            {formData.taskType === 'validacion' && (
              <div>
                <Label htmlFor="moduleTarget">Módulo de Validación *</Label>
                <select
                  id="moduleTarget"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.moduleTarget}
                  onChange={(e) => setFormData({ ...formData, moduleTarget: e.target.value })}
                  required
                >
                  <option value="">Seleccionar módulo...</option>
                  <option value="alumno/documentos">Alumno - Subir Documentos</option>
                  <option value="alumno/perfil">Alumno - Actualizar Perfil</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  La tarea se completará automáticamente cuando el usuario realice esta acción
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
              <Button type="submit">{selectedTask ? 'Actualizar' : 'Crear'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar Tarea</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <p className="text-sm text-gray-600 mb-4">
              Asignando: <strong>{selectedTask.title}</strong>
            </p>
          )}
          <form onSubmit={handleAssignTask} className="space-y-4">
            <div>
              <Label>Asignar a</Label>
              <div className="flex flex-col gap-2 mt-2">
                <label className="flex items-center gap-2">
                  <input type="radio" value="role" checked={assignData.assignmentType === 'role'} onChange={() => setAssignData({ ...assignData, assignmentType: 'role' })} />
                  <span>Rol completo</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" value="user" checked={assignData.assignmentType === 'user'} onChange={() => setAssignData({ ...assignData, assignmentType: 'user' })} />
                  <span>Usuario específico</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" value="all_users" checked={assignData.assignmentType === 'all_users'} onChange={() => setAssignData({ ...assignData, assignmentType: 'all_users' })} />
                  <span>Todos los usuarios</span>
                </label>
              </div>
            </div>
            {assignData.assignmentType === 'role' ? (
              <div>
                <Label htmlFor="roleId">Seleccionar Rol *</Label>
                <select id="roleId" className="w-full border rounded-md px-3 py-2" value={assignData.roleId} onChange={(e) => setAssignData({ ...assignData, roleId: e.target.value })} required>
                  <option value="">Seleccionar rol...</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
            ) : assignData.assignmentType === 'user' ? (
              <div>
                <Label htmlFor="userId">Seleccionar Usuario *</Label>
                <select id="userId" className="w-full border rounded-md px-3 py-2" value={assignData.userId} onChange={(e) => setAssignData({ ...assignData, userId: e.target.value })} required>
                  <option value="">Seleccionar usuario...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.fullName} ({user.email})</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">La tarea se asignará a <strong>todos los usuarios</strong> del sistema</p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>Cancelar</Button>
              <Button type="submit">Asignar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showResponseDialog} onOpenChange={(open) => { if (!open) setShowResponseDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Respuesta del Usuario</DialogTitle>
          </DialogHeader>
          {showResponseDialog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Usuario:</span>
                  <span className="text-sm text-gray-900">{showResponseDialog.userName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Tarea:</span>
                  <span className="text-sm text-gray-900">{showResponseDialog.taskTitle}</span>
                </div>
                {showResponseDialog.completedAt && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Completada:</span>
                    <span className="text-sm text-gray-900">{formatDate(showResponseDialog.completedAt)}</span>
                  </div>
                )}
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-xs text-green-700 font-medium mb-1">Respuesta:</p>
                <p className="text-sm text-green-900 whitespace-pre-wrap">{showResponseDialog.completionResponse}</p>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowResponseDialog(null)}>Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskManagement;
